import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Divider,
  Avatar,
  IconButton,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { getComments, addComment, hideComment } from "src/repos/comments";
import { Comment, ExpandMore, ExpandLess, Delete } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  commentInput: {
    marginBottom: theme.spacing(2),
  },
  commentList: {
    maxHeight: 500,
    overflowY: "auto",
  },
  commentItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
  },
  commentAvatar: {
    marginRight: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3),
    fontSize: "0.8rem",
  },
  newCommentSection: {
    marginTop: theme.spacing(2),
  },
}));

function Comments({ machine }) {
  const classes = useStyles();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [commentCount, setCommentCount] = useState(0);
  const [error, setError] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const handleDeleteClick = (commentId) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (commentToDelete) {
      try {
        await hideComment(commentToDelete);
        await fetchComments();
      } catch {
        setError(true);
      } finally {
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const { id: machineId } = machine || {};

  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getComments(machineId);
      setComments(response || []);
      setCommentCount(response?.length || 0);
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    if (machineId) {
      fetchComments();
    }
  }, [fetchComments, machineId]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addComment(machine.id, newComment);
      setNewComment("");
      await fetchComments();
    } catch {
      setError(true);
    }
  };

  return (
    <Paper className={classes.root} elevation={3}>
      <div className={classes.header}>
        <Comment color="primary" style={{ marginRight: 8 }} />
        <Typography variant="h6">
          Comments {!isLoading && !error && `(${commentCount})`}
        </Typography>
        <div style={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>

      <Collapse in={expanded}>
        {error ? (
          <Typography color="error">
            Could not load comments. Please try again later.
          </Typography>
        ) : isLoading ? (
          <Typography variant="body2">Loading comments...</Typography>
        ) : comments.length === 0 ? (
          <Typography variant="body2">No comments yet</Typography>
        ) : (
          <div className={classes.commentList}>
            <List dense>
              {comments.map((comment) => (
                <React.Fragment key={comment.id}>
                  <ListItem className={classes.commentItem}>
                    <ListItemText
                      primary={
                        <div className={classes.commentHeader}>
                          <Avatar className={classes.commentAvatar}>
                            {(comment.user_name || comment.user?.email || "")
                              .charAt(0)
                              .toUpperCase()}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {comment.user_name ||
                              comment.user?.email ||
                              "Unknown"}
                          </Typography>
                          <div style={{ flexGrow: 1 }} />
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(comment.id)}
                            aria-label="delete"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </div>
                      }
                      secondary={
                        <Typography variant="body2">{comment.text}</Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </div>
        )}

        {!error && (
          <div className={classes.newCommentSection}>
            <TextField
              label="Add comment"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className={classes.commentInput}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              Post
            </Button>
          </div>
        )}
      </Collapse>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Hide Comment</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this comment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default Comments;
